import { Component, ojs, app, state, v, payload } from "modular-openscriptjs";
import {
    pageInfo,
    formatThousand,
    formatDateForHumans,
    either,
    scrollToTop,
    isMobile,
    cleanDispose,
    inform,
    eParse,
    updateStateValue,
    randomLightGradient,
    empty,
    monetary,
} from "../../../helpers";
import { api, dc, STRINGS } from "../../../declarations";
import { $e } from "../../../ojs.events";
import SolidIcon from "../../Blocks/SolidIcon";
import OffcanvasBody from "../../Offcanvas/OffcanvasBody";
import ModalBody from "../../Modals/ModalBody";
import Spinner from "../../Blocks/Spinner";
import ViewOrder from "./ViewOrder";
import AudioPlayerView from "../../Views/Order/AudioPlayerView";
import { dom, tool } from "../../../fotastart";
import { Capacitor } from "@capacitor/core";
import { getFirebaseToken } from "../../../firebase";

const h = app("h");
const broker = app("broker");
const router = app("router");

ojs(SolidIcon, OffcanvasBody, ModalBody, Spinner, ViewOrder, AudioPlayerView);

export default class Orders extends Component {
    companyId = state(null);
    orders = state([]);
    loading = state(true);
    branches = state([]);
    loadingBranches = state(false);
    currentPage = state(1);
    paginationInfo = state(null);
    currentOrder = state(null);
    activeBranch = state("");
    swipeStartX = 0;
    swipeStartY = 0;
    swipeStartTime = 0;

    filters = state({
        branch_id: "",
        status: ["queued"],
        from_date: "",
        to_date: "",
    });

    filterHolder = null;
    cardGradients = {};

    getOrderGradient(orderId) {
        if (!this.cardGradients[orderId]) {
            this.cardGradients[orderId] = randomLightGradient();
        }
        return this.cardGradients[orderId];
    }

    $$order = {
        has: {
            businessOrders: (ed) => {
                let data = eParse(ed);
                this.orders.value = data.message.orders.data;
                this.paginationInfo.value = {
                    current_page: data.message.orders.current_page,
                    last_page: data.message.orders.last_page,
                    total: data.message.orders.total,
                };
                this.loading.value = false;
            },
            updatedStatus: () => {
                this.fetchOrders();
            },
        },
    };

    $_mounted() {
        if (Capacitor.isNativePlatform()) {
            getFirebaseToken();
        }
    }

    selectBranch(branchId) {
        this.activeBranch.value = branchId;
        updateStateValue(this.filters, (v) => {
            v.branch_id = branchId;
            return v;
        });
        this.currentPage.value = 1;
        this.fetchOrders();
    }

    selectStatus(statusKey) {
        updateStateValue(this.filters, (v) => {
            v.status = [statusKey];
            return v;
        });
        this.currentPage.value = 1;
        this.fetchOrders();

        setTimeout(() => {
            let s = dom.get(`[status-active="true"]`);

            if (s) {
                s.scrollIntoView({
                    behavior: "smooth",
                    inline: "center",
                    block: "nearest",
                });
            }
        }, 100);
    }

    selectPreviousStatus() {
        const currentStatus = this.filters.value.status[0] || "queued";
        const statusKeys = ["queued", "preparing", "ready", "delivering"];
        const currentIndex = statusKeys.indexOf(currentStatus);

        let prevIndex = currentIndex - 1;
        if (prevIndex < 0) {
            prevIndex = statusKeys.length - 1;
        }

        this.selectStatus(statusKeys[prevIndex]);
    }

    selectNextStatus() {
        const currentStatus = this.filters.value.status[0] || "queued";
        const statusKeys = ["queued", "preparing", "ready", "delivering"];
        const currentIndex = statusKeys.indexOf(currentStatus);

        let nextIndex = currentIndex + 1;
        if (nextIndex >= statusKeys.length) {
            nextIndex = 0;
        }

        this.selectStatus(statusKeys[nextIndex]);
    }

    onPointerDown(e) {
        if (!e.isPrimary) return;
        this.swipeStartX = e.clientX;
        this.swipeStartY = e.clientY;
        this.swipeStartTime = Date.now();
        
        try {
            e.target.setPointerCapture(e.pointerId);
        } catch (err) {}
    }

    onPointerUp(e) {
        if (!e.isPrimary) return;
        
        try {
            e.target.releasePointerCapture(e.pointerId);
        } catch (err) {}

        const deltaX = e.clientX - this.swipeStartX;
        const deltaY = e.clientY - this.swipeStartY;
        const duration = Date.now() - this.swipeStartTime;

        if (Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY) && duration < 800) {
            if (deltaX > 0) {
                this.selectPreviousStatus();
            } else {
                this.selectNextStatus();
            }
        }
    }

    renderBranchSelection() {
        if (this.branches.value.length === 0 && !this.loadingBranches.value) {
            this.fetchBranches();
        }

        return h.div(
            {
                class: "container d-flex flex-column align-items-center justify-content-center py-5 py-md-5",
            },
            h.div(
                {
                    class: "card border-0 shadow-lg p-5 rounded-5 text-center bg-white bg-opacity-75",
                    style: "max-width: 500px; backdrop-filter: blur(15px); border: 1px solid rgba(255, 255, 255, 0.4);",
                },
                h.div(
                    { class: "mb-4 text-primary" },
                    h.i({ class: "bi bi-geo-alt-fill fs-1 display-4" }),
                ),
                h.h3(
                    { class: "fw-bold mb-2 text-dark" },
                    "Select Operating Branch",
                ),
                h.p(
                    { class: "text-muted mb-4 fs-6" },
                    "Choose the branch you want to manage orders for:",
                ),

                v(this.loadingBranches, (lBr) => {
                    if (lBr.value) {
                        return h.div(
                            { class: "py-4 text-center" },
                            h.Spinner(),
                        );
                    }

                    return v(this.branches, (branches) => {
                        if (branches.value.length === 0) {
                            return h.div(
                                { class: "text-center py-3" },
                                h.p(
                                    { class: "text-secondary" },
                                    "No branches found. Please create one in company settings.",
                                ),
                            );
                        }

                        return h.div(
                            { class: "d-grid gap-3 w-100 mt-2" },
                            ...branches.value.map((b) =>
                                h.button(
                                    {
                                        class: "btn btn-outline-primary p-3 rounded-4 d-flex align-items-center justify-content-between text-start border-2 hover-lift transition-all",
                                        listeners: {
                                            click: () => {
                                                this.selectBranch(b.id);
                                            },
                                        },
                                    },
                                    h.div(
                                        {},
                                        h.div(
                                            { class: "fw-bold fs-6" },
                                            b.name,
                                        ),
                                        b.readable_address
                                            ? h.small(
                                                  { class: "text-muted" },
                                                  b.readable_address,
                                              )
                                            : null,
                                    ),
                                    h.i({ class: "bi bi-chevron-right" }),
                                ),
                            ),
                        );
                    });
                }),
            ),
        );
    }

    renderStickyStatusCard() {
        const statusList = [
            { key: "queued", label: "Waiting" },
            { key: "preparing", label: "Preparing" },
            { key: "ready", label: "Ready" },
            { key: "delivering", label: "Picked" },
        ];

        return v(this.filters, (filt) => {
            const currentStatus = filt.value.status[0] || "queued";

            return h.div(
                {
                    class: "card border-0 shadow-sm rounded-4 mb-4 sticky-top bg-white bg-opacity-95",
                    style: "top: 65px; z-index: 1010; backdrop-filter: blur(10px); border: 1px solid rgba(0,0,0,0.05) !important;",
                },
                h.div(
                    {
                        class: "card-body p-2 d-flex align-items-center justify-content-between gap-2",
                    },
                    h.button(
                        {
                            class: "btn btn-white border circle shadow-sm p-2 d-flex align-items-center justify-content-center",
                            style: "width: 40px; height: 40px;",
                            listeners: {
                                click: () => this.selectPreviousStatus(),
                            },
                        },
                        h.i({ class: "bi bi-chevron-left fw-bold" }),
                    ),

                    h.div(
                        {
                            class: "d-flex gap-2 flex-grow-1 overflow-x-auto no-scrollbar py-1 align-items-center justify-content-md-center",
                            style: "scrollbar-width: none; -ms-overflow-style: none;",
                        },
                        ...statusList.map((status) => {
                            const isActive = currentStatus === status.key;
                            const getActiveStatusStyles = (key) => {
                                switch (key) {
                                    case "queued": 
                                        return { class: "btn-secondary text-white shadow-sm scale-up", style: "" };
                                    case "preparing": 
                                        return { class: "text-white shadow-sm scale-up", style: "background-color: #0d6efd !important; border-color: #0d6efd !important;" };
                                    case "ready": 
                                        return { class: "btn-warning text-dark shadow-sm scale-up", style: "" };
                                    case "delivering": 
                                        return { class: "btn-success text-white shadow-sm scale-up", style: "" };
                                    default: 
                                        return { class: "btn-primary text-white shadow-sm scale-up", style: "" };
                                }
                            };
                            
                            const activeStyles = isActive ? getActiveStatusStyles(status.key) : null;
                            const btnAttrs = {
                                status_active: currentStatus === status.key,
                                class: `btn rounded-pill px-4 py-2.5 fw-bold text-nowrap transition-all ${
                                    isActive
                                        ? activeStyles.class
                                        : "btn-light text-secondary hover-light"
                                }`,
                                listeners: {
                                    click: () =>
                                        this.selectStatus(status.key),
                                },
                            };

                            if (isActive && activeStyles.style) {
                                btnAttrs.style = activeStyles.style;
                            }

                            return h.button(
                                btnAttrs,
                                status.label,
                            );
                        }),
                    ),

                    h.button(
                        {
                            class: "btn btn-white border circle shadow-sm p-2 d-flex align-items-center justify-content-center",
                            style: "width: 40px; height: 40px;",
                            listeners: { click: () => this.selectNextStatus() },
                        },
                        h.i({ class: "bi bi-chevron-right fw-bold" }),
                    ),
                ),
            );
        });
    }

    renderOrdersPage() {
        return h.div(
            { class: "min-vh-100 py-4 pb-5 mb-5 py-md-5" },
            h.div(
                { class: "container" },
                // Header
                h.div(
                    {
                        class: "mb-4 d-flex justify-content-between align-items-center",
                    },
                    h.div(
                        { class: "d-flex align-items-center gap-3" },
                        h.button(
                            {
                                class: "btn btn-white border shadow-sm circle p-2",
                                onclick: "app('router').back()",
                            },
                            h.SolidIcon("arrow-left"),
                        ),
                        h.div(
                            {},
                            h.div(
                                {
                                    class: "text-muted small text-uppercase fw-bold mb-1",
                                },
                                "Managing Orders For",
                            ),
                            v(this.activeBranch, (actBr) => {
                                const branch = this.branches.value.find(
                                    (b) => b.id == actBr.value,
                                );
                                return h.h2(
                                    {
                                        class: "fw-bold mb-0 h4 h2-md d-flex align-items-center gap-2 flex-wrap",
                                    },
                                    branch ? branch.name : "Select Branch",
                                    h.span(
                                        { class: "text-muted fs-6 fw-normal d-inline-flex align-items-center gap-1" },
                                    )
                                );
                            }),
                        ),
                    ),
                    h.button(
                        {
                            class: "btn btn-outline-primary rounded-pill px-4 shadow-sm d-flex align-items-center gap-2",
                            listeners: {
                                click: () => {
                                    this.activeBranch.value = "";
                                },
                            },
                        },
                        h.i({ class: "bi bi-arrow-left-right" }),
                        h.span(
                            { class: "d-none d-md-inline" },
                            "Switch Branch",
                        ),
                    ),
                ),

                // Sticky status scrollbar card
                this.renderStickyStatusCard(),

                // Content
                h.div(
                    {
                        style: "touch-action: pan-y; min-height: 400px;",
                        listeners: {
                            pointerdown: (e) => this.onPointerDown(e),
                            pointerup: (e) => this.onPointerUp(e),
                        }
                    },
                    v(this.loading, (l) => {
                        if (l.value)
                            return h.div(
                                { class: "text-center py-5" },
                                h.Spinner(),
                            );

                        return v(this.orders, (o) => {
                            if (o.value.length === 0) {
                                const currentStatus = this.filters.value.status[0] || "queued";
                                const getEmptyStateContent = (statusKey) => {
                                    switch (statusKey) {
                                        case "queued":
                                            return {
                                                title: "No waiting orders",
                                                desc: "All caught up! Wait for customers to place new orders."
                                            };
                                        case "preparing":
                                            return {
                                                title: "No orders in preparation",
                                                desc: "Start preparing waiting orders to see them here."
                                            };
                                        case "ready":
                                            return {
                                                title: "No orders ready for pickup",
                                                desc: "Orders waiting for pickup or delivery will appear here."
                                            };
                                        case "delivering":
                                            return {
                                                title: "No picked up orders",
                                                desc: "Orders currently in transit or picked up by riders."
                                            };
                                        default:
                                            return {
                                                title: "No orders found",
                                                desc: "No orders found matching the selected status."
                                            };
                                    }
                                };
                                const emptyContent = getEmptyStateContent(currentStatus);

                                return h.div(
                                    {
                                        class: "card border-0 shadow-sm rounded-4 p-5 text-center bg-white",
                                    },
                                    h.div(
                                        {
                                            class: "bg-light rounded-circle p-4 mx-auto mb-3",
                                            style: "width: 80px; height: 80px;",
                                        },
                                        h.i({
                                            class: "bi bi-cart-x fs-1 text-muted opacity-50",
                                        }),
                                    ),
                                    h.h5({ class: "fw-bold mb-2" }, emptyContent.title),
                                    h.p(
                                        { class: "text-muted mb-3" },
                                        emptyContent.desc,
                                    ),
                                    h.small(
                                        { class: "text-secondary d-flex align-items-center justify-content-center gap-2 opacity-75 fw-semibold" },
                                        h.i({ class: "bi bi-chevron-left" }),
                                        "Swipe to change status",
                                        h.i({ class: "bi bi-chevron-right" }),
                                    ),
                                );
                            }

                            return h.div(
                                {},
                                h.div(
                                    { class: "row g-4 mb-4" },
                                    ...o.value.map((order) =>
                                        this.renderOrderCard(order),
                                    ),
                                ),
                                this.renderPagination(),
                            );
                        });
                    }),
                ),
            ),
        );
    }

    render(companyIdState) {
        scrollToTop();

        if (companyIdState) {
            this.companyId.value = companyIdState.value;
        }

        pageInfo("Manage Orders");

        if (
            this.companyId.value &&
            this.branches.value.length === 0 &&
            !this.loadingBranches.value
        ) {
            this.fetchBranches();
        }

        return v(this.activeBranch, (activeBr) => {
            if (empty(activeBr.value)) {
                return this.renderBranchSelection();
            }

            return this.renderOrdersPage();
        });
    }

    renderOrderCard(order) {
        return h.div(
            { class: "col-12 col-md-6 col-lg-4" },
            h.div(
                {
                    class: "card border-0 shadow-sm rounded-4 h-100 hover-shadow transition-all overflow-hidden",
                    style: "background: rgba(255, 255, 255, 0.8); backdrop-filter: blur(10px); border-top: 4px solid var(--bs-primary) !important;",
                },
                h.div(
                    { class: "card-body p-4" },
                    // Header: Code & Status
                    h.div(
                        {
                            class: "d-flex justify-content-between align-items-start mb-4",
                        },
                        h.div(
                            {},
                            h.div(
                                {
                                    class: "text-muted small text-uppercase fw-bold mb-1",
                                    style: "letter-spacing: 1px;",
                                },
                                "Order Code",
                            ),
                            h.h5(
                                { class: "fw-bold mb-1 text-dark" },
                                `${order.order_code || "#" + order.id}`,
                            ),
                            h.small(
                                { class: "text-muted" },
                                formatDateForHumans(order.created_at),
                            ),
                        ),
                        h.button(
                            {
                                class: "btn btn-sm btn-white border rounded-3 px-3 py-2 fw-bold shadow-sm d-flex align-items-center gap-2 text-secondary",
                                onclick: this.method("openOrderDetails", order),
                            },
                            h.i({ class: "bi bi-eye-fill fs-6" }),
                            "View",
                        ),
                    ),

                    // Timing Info
                    v(order.processing_due_at, (due) => {
                        if (!due?.value) return null;
                        return h.div(
                            {
                                class: "mb-4 alert alert-warning border-0 rounded-4 p-3 d-flex align-items-center gap-3",
                            },
                            h.i({ class: "bi bi-clock-history fs-4" }),
                            h.div(
                                {},
                                h.div(
                                    { class: "small fw-bold" },
                                    "Processing Target",
                                ),
                                h.div(
                                    { class: "small" },
                                    formatDateForHumans(due?.value),
                                ),
                            ),
                        );
                    }),

                    // Financial & Processing Time
                    h.div(
                        {
                            class: "mb-4 d-flex justify-content-between align-items-center",
                        },
                        h.div(
                            { class: "d-flex gap-4" },
                            h.div(
                                {},
                                h.div(
                                    {
                                        class: "small text-muted text-uppercase fw-bold mb-1",
                                        style: "font-size: 0.7rem;",
                                    },
                                    "Net Total",
                                ),
                                h.div(
                                    { class: "fw-bold fs-6 text-success" },
                                    monetary(
                                        order.company_total_revenue,
                                        dc.userCurrency,
                                    ),
                                ),
                            ),
                        ),
                        either(
                            order.company_preparation_time ||
                                order.delivery_time,
                            () =>
                                h.div(
                                    { class: "text-end" },
                                    h.div(
                                        {
                                            class: "small text-muted text-uppercase fw-bold mb-1",
                                            style: "font-size: 0.7rem;",
                                        },
                                        "Preparation Time",
                                    ),
                                    h.div(
                                        {
                                            class: "small fw-bold text-secondary",
                                        },
                                        h.i({
                                            class: "bi bi-clock me-1 text-warning",
                                        }),
                                        order.company_preparation_time
                                            ? `${order.company_preparation_time} mins`
                                            : `${Math.round(order.delivery_time / 60)} mins`,
                                    ),
                                ),
                        ),
                    ),

                    // Actions
                    h.div(
                        { class: "d-flex gap-2 w-100" },
                        // Speaker Button (TTS)
                        either(
                            !empty(order?.pivot?.audio_path) ||
                            (Array.isArray(order?.branch_audios) && order.branch_audios.length > 0),
                            () =>
                            h.button(
                                {
                                    class: "btn rounded-3 px-3 shadow-sm d-flex align-items-center justify-content-center gap-2 py-2 fw-semibold",
                                    style: "background: linear-gradient(135deg, rgba(13, 202, 240, 0.15), rgba(13, 110, 253, 0.15)); color: #0959d6; border: 1px solid rgba(13, 110, 253, 0.25); flex: 1;",
                                    title: "Listen to Order",
                                    onclick: this.method(
                                        "readOrderAloud",
                                        order,
                                    ),
                                },
                                h.i({ class: "bi bi-volume-up-fill fs-5" }),
                                "Listen",
                            ),
                        ),
                        // Start Preparing Button
                        order.company_status === "queued"
                            ? h.button(
                                  {
                                      class: "btn rounded-pill px-3 shadow-sm d-flex align-items-center justify-content-center gap-2 py-2 fw-bold text-success",
                                      style: "background: linear-gradient(135deg, rgba(25, 135, 84, 0.08), rgba(32, 201, 151, 0.08)); border: 1px solid rgba(25, 135, 84, 0.25); flex: 2.2; box-shadow: 0 4px 15px rgba(25, 135, 84, 0.04);",
                                      title: "Start Preparing",
                                      onclick: this.method(
                                          "markAsProcessing",
                                          order.id,
                                      ),
                                  },
                                  h.i({
                                      class: "bi bi-play-fill fs-5 text-success",
                                  }),
                                  "Start Preparing",
                              )
                            : null,
                        // Ready Button
                        order.company_status === "preparing"
                            ? h.button(
                                  {
                                      class: "btn rounded-pill px-3 shadow-sm d-flex align-items-center justify-content-center gap-2 py-2 fw-bold text-white border-0",
                                      style: "background: linear-gradient(135deg, #198754, #20c997); flex: 2.2; box-shadow: 0 4px 15px rgba(25, 135, 84, 0.2);",
                                      title: "Mark as Ready",
                                      onclick: this.method(
                                          "markAsReady",
                                          order.id,
                                      ),
                                  },
                                  h.i({
                                      class: "bi bi-check-circle-fill fs-5",
                                  }),
                                  "Ready For Pickup",
                              )
                            : null,
                    ),
                ),
            ),
        );
    }

    getStatusColor(status) {
        switch (status) {
            case "picked":
                return "success";
            case "pending":
                return "warning";
            case "queued":
                return "primary";
            case "preparing":
                return "info";
            case "ready":
                return "indigo";
            case "failed":
                return "danger";
            default:
                return "secondary";
        }
    }

    async fetchOrders() {
        this.loading.value = true;
        broker.broadcast(
            $e.order.needs.businessOrders,
            payload({
                companyId: router.params.companyId,
                filters: this.filters.value,
                page: this.currentPage.value,
                interval: 5,
            }),
        );
    }

    async fetchBranches() {
        this.loadingBranches.value = true;
        try {
            const res = await api.get(
                `companies/${router.params.companyId}/branches`,
            );
            if (res) {
                this.branches.value = res.data.branches;
                if (res.data.branches.length === 1) {
                    this.selectBranch(res.data.branches[0].id);
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            this.loadingBranches.value = false;
        }
    }

    openFilterForm() {
        if (this.filterHolder) cleanDispose(this.filterHolder);

        const form = () => this.renderFilterForm();

        if (isMobile()) {
            this.filterHolder = tool.offcanvas(h.OffcanvasBody(form()), {
                class: "rounded-top-5 shadow-lg",
                style: "height: auto; max-height: 85vh;",
            });
        } else {
            this.filterHolder = tool.plainModal(h.ModalBody(form()));
        }

        this.filterHolder.show();
    }

    openOrderDetails(order) {
        if (this.orderDetailsHolder) cleanDispose(this.orderDetailsHolder);
        this.currentOrder.value = order;

        const details = () => h.ViewOrder(this.currentOrder);

        if (isMobile()) {
            this.orderDetailsHolder = tool.offcanvas(
                h.OffcanvasBody(details()),
                {
                    class: "rounded-top-5 shadow-lg",
                    style: "height: auto; max-height: 90vh;",
                },
            );
        } else {
            this.orderDetailsHolder = tool.plainModal(h.ModalBody(details()));
        }

        this.orderDetailsHolder.show();
    }

    renderFilterForm() {
        return h.div(
            { class: "p-3" },
            h.div(
                {
                    class: "d-flex justify-content-between align-items-center mb-4",
                },
                h.h4({ class: "fw-bold mb-0" }, "Filter Orders"),
                h.button({
                    class: "btn-close",
                    listeners: { click: () => this.filterHolder.hide() },
                }),
            ),

            h.div(
                { class: "d-grid gap-4" },
                // Branch
                h.div(
                    {},
                    h.label(
                        {
                            class: "form-label fw-bold small text-muted text-uppercase",
                        },
                        "Branch",
                    ),
                    h.select(
                        {
                            class: "form-select p-3 rounded-3 border bg-light",
                            listeners: {
                                change: (e) => {
                                    const val = e.target.value;
                                    this.activeBranch.value = val;
                                    updateStateValue(this.filters, (v) => {
                                        v.branch_id = val;
                                        return v;
                                    });
                                },
                            },
                        },
                        h.option({ value: "" }, "All Branches"),
                        ...this.branches.value.map((b) =>
                            h.option(
                                { value: b.id },
                                either(this.filters.value.branch_id == b.id, {
                                    selected: true,
                                }),
                                b.name,
                            ),
                        ),
                    ),
                ),

                // Status
                h.div(
                    h.label(
                        {
                            class: "form-label fw-bold small text-muted text-uppercase",
                        },
                        "Order Status",
                    ),
                    h.select(
                        {
                            class: "form-select p-3 rounded-3 border bg-light",
                            listeners: {
                                change: (e) => {
                                    updateStateValue(this.filters, (v) => {
                                        v.status = [e.target.value] || "queued";
                                        return v;
                                    });
                                },
                            },
                        },
                        h.option(
                            {
                                value: "queued",
                            },
                            either(this.filters.value.status[0] === "queued", {
                                selected: true,
                            }),
                            "In Queue",
                        ),
                        h.option(
                            {
                                value: "preparing",
                            },
                            either(
                                this.filters.value.status[0] === "preparing",
                                {
                                    selected: true,
                                },
                            ),
                            "Preparing",
                        ),
                        h.option(
                            {
                                value: "ready",
                            },
                            either(this.filters.value.status[0] === "ready", {
                                selected: true,
                            }),
                            "Ready for Pickup",
                        ),
                        h.option(
                            {
                                value: "delivering",
                            },
                            either(
                                this.filters.value.status[0] === "delivering",
                                {
                                    selected: true,
                                },
                            ),
                            "Picked Up",
                        ),
                    ),
                ),

                // Date Range
                h.div(
                    { class: "row g-3" },
                    h.div(
                        { class: "col-6" },
                        h.label(
                            {
                                class: "form-label fw-bold small text-muted text-uppercase",
                            },
                            "From Date",
                        ),
                        h.input({
                            type: "date",
                            class: "form-control p-3 rounded-3 border bg-light",
                            value: this.filters.value.from_date,
                            listeners: {
                                change: (e) => {
                                    updateStateValue(this.filters, (v) => {
                                        v.from_date = e.target.value;
                                        return v;
                                    });
                                },
                            },
                        }),
                    ),
                    h.div(
                        { class: "col-6" },
                        h.label(
                            {
                                class: "form-label fw-bold small text-muted text-uppercase",
                            },
                            "To Date",
                        ),
                        h.input({
                            type: "date",
                            class: "form-control p-3 rounded-3 border bg-light",
                            value: this.filters.value.to_date,
                            listeners: {
                                change: (e) => {
                                    updateStateValue(this.filters, (v) => {
                                        v.to_date = e.target.value;
                                        return v;
                                    });
                                },
                            },
                        }),
                    ),
                ),

                // Apply Button
                h.button(
                    {
                        class: "btn btn-primary rounded-pill p-3 fw-bold shadow-sm mt-2",
                        listeners: {
                            click: () => {
                                this.currentPage.value = 1;
                                this.fetchOrders();
                                this.filterHolder.hide();
                            },
                        },
                    },
                    "Apply Filters",
                ),
                // Reset Button
                h.button(
                    {
                        class: "btn btn-light rounded-pill p-3 fw-bold",
                        listeners: {
                            click: () => {
                                this.filters.value = {
                                    branch_id: this.activeBranch.value,
                                    status: ["queued"],
                                    from_date: "",
                                    to_date: "",
                                };
                                this.currentPage.value = 1;
                                this.fetchOrders();
                                this.filterHolder.hide();
                            },
                        },
                    },
                    "Reset All",
                ),
            ),
        );
    }

    markAsProcessing(orderId) {
        broker.send(
            $e.order.needs.markAsProcessing,
            payload({
                companyId: router.params.companyId,
                orderId: orderId,
                branchId: this.activeBranch.value,
            }),
        );
    }

    markAsReady(orderId) {
        broker.send(
            $e.order.needs.markAsReady,
            payload({
                companyId: router.params.companyId,
                orderId: orderId,
                branchId: this.activeBranch.value,
            }),
        );
    }

    audioPlayerHolder = null;

    readOrderAloud(order) {
        if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
        }

        if (this.audioPlayerHolder) cleanDispose(this.audioPlayerHolder);

        const player = () => h.AudioPlayerView(order, this.activeBranch.value);

        if (isMobile()) {
            this.audioPlayerHolder = tool.offcanvas(h.OffcanvasBody(player()), {
                class: "rounded-top-5 shadow-lg",
                style: "height: auto; max-height: 95vh;",
            });
        } else {
            this.audioPlayerHolder = tool.plainModal(h.ModalBody(player()));
        }

        this.audioPlayerHolder.show();
    }

    renderPagination() {
        return v(this.paginationInfo, (info) => {
            if (!info.value || info.value.last_page <= 1) return h.empty();

            const currentPage = info.value.current_page;
            const lastPage = info.value.last_page;

            return h.div(
                {
                    class: "d-flex justify-content-between align-items-center mt-4 bg-white p-3 rounded-4 shadow-sm border border-light-subtle",
                },
                h.button(
                    {
                        class: "btn btn-sm btn-outline-primary rounded-pill fw-semibold d-flex align-items-center gap-2",
                        listeners: {
                            click: () => {
                                if (currentPage > 1) {
                                    this.currentPage.value = currentPage - 1;
                                    this.fetchOrders();
                                    scrollToTop();
                                }
                            },
                        },
                    },
                    either(currentPage === 1, { disabled: true }),
                    h.i({ class: "bi bi-arrow-left" }),
                    "Previous",
                ),
                h.span(
                    { class: "fw-bold text-secondary fs-6" },
                    `Page ${currentPage} of ${lastPage}`,
                ),
                h.button(
                    {
                        class: "btn btn-sm btn-outline-primary rounded-pill fw-semibold d-flex align-items-center gap-2",
                        listeners: {
                            click: () => {
                                if (currentPage < lastPage) {
                                    this.currentPage.value = currentPage + 1;
                                    this.fetchOrders();
                                    scrollToTop();
                                }
                            },
                        },
                    },
                    either(currentPage === lastPage, { disabled: true }),
                    "Next",
                    h.i({ class: "bi bi-arrow-right" }),
                ),
            );
        });
    }
}

ojs(Orders);
